import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'

import { api } from '../services/api'
import { LoadingSpinner } from '../components/LoadingSpinner'

// Types for toolkits
interface Tool {
  name: string
  description: string
  inputSchema: {
    type: string
    properties: Record<string, any>
  }
}

interface Toolkit {
  id: string
  name: string
  description: string
  category: string
  tools: Tool[]
  healthStatus: 'healthy' | 'unhealthy' | 'unreachable'
  url: string
  docsUrl: string
  openApiUrl: string
  features: string[]
  pricing: {
    free: boolean
    monthly?: number
    yearly?: number
    oneTime?: number
  }
  isPopular?: boolean
  isFeatured?: boolean
}

interface ToolkitCategory {
  id: string
  name: string
  description: string
  icon: string
}

interface ToolkitFilters {
  category: string
  search: string
  priceRange: string
  sortBy: string
}

// Mock data fallback function
function getMockToolkits(): Toolkit[] {
  return [
    {
      id: 'plexabyss',
      name: 'PlexAbyss',
      description: 'Comprehensive media server management with Plex, Radarr, Sonarr integration',
      category: 'media',
      tools: [
        { name: 'get_recently_added', description: 'Get recently added media items', inputSchema: { type: 'object', properties: {} } },
        { name: 'search_content', description: 'Search for movies and TV shows', inputSchema: { type: 'object', properties: { query: { type: 'string' } } } },
        { name: 'manage_requests', description: 'Handle media requests and approvals', inputSchema: { type: 'object', properties: {} } }
      ],
      healthStatus: 'healthy',
      url: 'https://tools.nickfixit.com/plexabyss',
      docsUrl: 'https://tools.nickfixit.com/plexabyss/docs',
      openApiUrl: 'https://tools.nickfixit.com/plexabyss/openapi.json',
      features: ['22 available tools', 'REST API access', 'Media server integration', 'Automated downloads'],
      pricing: { free: true },
      isPopular: true,
      isFeatured: true
    },
    {
      id: 'ytdlp',
      name: 'YT-DLP',
      description: 'Video downloading and processing with format conversion',
      category: 'media',
      tools: [
        { name: 'download_video', description: 'Download videos from supported platforms', inputSchema: { type: 'object', properties: { url: { type: 'string' } } } },
        { name: 'get_video_info', description: 'Extract video metadata and formats', inputSchema: { type: 'object', properties: { url: { type: 'string' } } } },
        { name: 'extract_audio', description: 'Extract audio from video files', inputSchema: { type: 'object', properties: { url: { type: 'string' } } } }
      ],
      healthStatus: 'healthy',
      url: 'https://tools.nickfixit.com/ytdlp',
      docsUrl: 'https://tools.nickfixit.com/ytdlp/docs',
      openApiUrl: 'https://tools.nickfixit.com/ytdlp/openapi.json',
      features: ['4 available tools', 'REST API access', 'Video downloads', 'Format conversion'],
      pricing: { free: true },
      isPopular: true
    },
    {
      id: 'obsidian-abyss',
      name: 'Obsidian Abyss',
      description: 'Advanced markdown documentation management for Abyss platform',
      category: 'productivity',
      tools: [
        { name: 'search_notes', description: 'Search through documentation', inputSchema: { type: 'object', properties: { query: { type: 'string' } } } },
        { name: 'create_note', description: 'Create new documentation', inputSchema: { type: 'object', properties: { title: { type: 'string' }, content: { type: 'string' } } } },
        { name: 'update_note', description: 'Update existing documentation', inputSchema: { type: 'object', properties: { path: { type: 'string' }, content: { type: 'string' } } } }
      ],
      healthStatus: 'healthy',
      url: 'https://tools.nickfixit.com/obsidian-abyss',
      docsUrl: 'https://tools.nickfixit.com/obsidian-abyss/docs',
      openApiUrl: 'https://tools.nickfixit.com/obsidian-abyss/openapi.json',
      features: ['8 available tools', 'REST API access', 'Markdown editing', 'Wikilink support'],
      pricing: { free: true },
      isFeatured: true
    },
    {
      id: 'kdeconnect',
      name: 'KDE Connect',
      description: 'Device communication, notifications, and file sharing',
      category: 'communication',
      tools: [
        { name: 'list_devices', description: 'List all paired devices', inputSchema: { type: 'object', properties: {} } },
        { name: 'send_notification', description: 'Send notifications to devices', inputSchema: { type: 'object', properties: { device_id: { type: 'string' }, title: { type: 'string' }, message: { type: 'string' } } } },
        { name: 'share_file', description: 'Share files with devices', inputSchema: { type: 'object', properties: { device_id: { type: 'string' }, file_path: { type: 'string' } } } }
      ],
      healthStatus: 'healthy',
      url: 'https://tools.nickfixit.com/kdeconnect',
      docsUrl: 'https://tools.nickfixit.com/kdeconnect/docs',
      openApiUrl: 'https://tools.nickfixit.com/kdeconnect/openapi.json',
      features: ['7 available tools', 'REST API access', 'Device pairing', 'File sharing'],
      pricing: { free: true },
      isPopular: true
    }
  ]
}

// Toolkit Card Component
function ToolkitCard({ toolkit }: { toolkit: Toolkit }) {
  const statusColors = {
    healthy: 'bg-green-100 text-green-800',
    unhealthy: 'bg-yellow-100 text-yellow-800',
    unreachable: 'bg-red-100 text-red-800'
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{toolkit.name}</h3>
            {toolkit.isFeatured && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Featured</span>
            )}
            {toolkit.isPopular && (
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Popular</span>
            )}
          </div>
          <span className={`inline-block text-xs px-2 py-1 rounded-full ${statusColors[toolkit.healthStatus]}`}>
            {toolkit.healthStatus}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{toolkit.description}</p>

      {/* Tools count */}
      <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
        <span>{toolkit.tools.length} tools</span>
        <span className="capitalize">{toolkit.category}</span>
      </div>

      {/* Features */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1">
          {toolkit.features.slice(0, 3).map((feature, index) => (
            <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
              {feature}
            </span>
          ))}
          {toolkit.features.length > 3 && (
            <span className="text-xs text-gray-500">+{toolkit.features.length - 3} more</span>
          )}
        </div>
      </div>

      {/* Pricing */}
      <div className="flex items-center justify-between">
        <div className="text-sm">
          {toolkit.pricing.free ? (
            <span className="text-green-600 font-medium">Free</span>
          ) : (
            <span className="text-gray-900 font-medium">${toolkit.pricing.monthly}/mo</span>
          )}
        </div>
        <div className="flex gap-2">
          <a
            href={toolkit.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs bg-gray-900 text-white px-3 py-1 rounded hover:bg-gray-800 transition-colors"
          >
            View Docs
          </a>
        </div>
      </div>
    </div>
  )
}

export function ToolkitsPage() {
  const [filters, setFilters] = useState<ToolkitFilters>({
    category: 'all',
    search: '',
    priceRange: 'all',
    sortBy: 'popular'
  })

  // Fetch all toolkits
  const {
    data: toolkits = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['toolkits'],
    queryFn: async () => {
      try {
        return await api.toolkits.getToolkits()
      } catch (error) {
        console.warn('Toolkits API failed, using mock data:', error)
        return getMockToolkits()
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Fetch categories
  const {
    data: categories = [],
    isLoading: categoriesLoading
  } = useQuery({
    queryKey: ['toolkit-categories'],
    queryFn: async () => {
      try {
        return await api.toolkits.getCategories()
      } catch (error) {
        console.warn('Categories API failed, using fallback:', error)
        // Fallback to static categories
        return [
          {
            id: 'all',
            name: 'All Toolkits',
            description: 'Browse all available toolkits',
            icon: '🔧'
          },
          {
            id: 'media',
            name: 'Media & Entertainment',
            description: 'Video, music, and content management',
            icon: '🎬'
          },
          {
            id: 'productivity',
            name: 'Productivity',
            description: 'Notes, documents, and organization',
            icon: '📝'
          },
          {
            id: 'system',
            name: 'System & Infrastructure',
            description: 'Database, monitoring, and system tools',
            icon: '⚙️'
          },
          {
            id: 'communication',
            name: 'Communication',
            description: 'Messaging, notifications, and collaboration',
            icon: '💬'
          },
          {
            id: 'ai',
            name: 'AI & Machine Learning',
            description: 'AI tools and model management',
            icon: '🤖'
          }
        ]
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  })

  // Filter and sort toolkits
  const filteredAndSortedToolkits = useMemo(() => {
    let filtered = toolkits

    // Filter by search term
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(toolkit =>
        toolkit.name.toLowerCase().includes(searchLower) ||
        toolkit.description.toLowerCase().includes(searchLower) ||
        toolkit.features.some(feature =>
          feature.toLowerCase().includes(searchLower)
        ) ||
        toolkit.tools.some(tool =>
          tool.name.toLowerCase().includes(searchLower) ||
          tool.description.toLowerCase().includes(searchLower)
        )
      )
    }

    // Filter by category
    if (filters.category !== 'all') {
      filtered = filtered.filter(toolkit => toolkit.category === filters.category)
    }

    // Filter by price range
    if (filters.priceRange === 'free') {
      filtered = filtered.filter(toolkit => toolkit.pricing?.free)
    } else if (filters.priceRange === 'paid') {
      filtered = filtered.filter(toolkit => !toolkit.pricing?.free)
    }

    // Sort toolkits
    return filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'tools_count':
          return b.tools.length - a.tools.length
        case 'popular':
        default:
          // Featured first, then popular, then by name
          if (a.isFeatured && !b.isFeatured) return -1
          if (!a.isFeatured && b.isFeatured) return 1
          if (a.isPopular && !b.isPopular) return -1
          if (!a.isPopular && b.isPopular) return 1
          return a.name.localeCompare(b.name)
      }
    })
  }, [toolkits, filters])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              MCP Toolkits
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful Model Context Protocol toolkits for AI assistants.
              From media management to productivity tools, enhance your AI capabilities.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search toolkits..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Sort by:</span>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="popular">Featured & Popular</option>
                <option value="name">Name</option>
                <option value="tools_count">Tool Count</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-gray-600">
            {isLoading ? (
              'Loading toolkits...'
            ) : (
              `Showing ${filteredAndSortedToolkits.length} of ${toolkits.length} toolkits`
            )}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-600 mb-4">Failed to load toolkits</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* No Results */}
        {!isLoading && !error && filteredAndSortedToolkits.length === 0 && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="text-gray-400 mb-4">
                <FunnelIcon className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No toolkits found
              </h3>
              <p className="text-gray-600 mb-4">
                Try adjusting your filters or search terms.
              </p>
              <button
                onClick={() => setFilters({ category: 'all', search: '', priceRange: 'all', sortBy: 'popular' })}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Toolkits Grid */}
        {!isLoading && !error && filteredAndSortedToolkits.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredAndSortedToolkits.map((toolkit) => (
              <ToolkitCard key={toolkit.id} toolkit={toolkit} />
            ))}
          </div>
        )}

        {/* Call to Action */}
        {!isLoading && !error && filteredAndSortedToolkits.length > 0 && (
          <div className="mt-16 text-center">
            <div className="bg-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">
                Need API access?
              </h2>
              <p className="text-blue-700 mb-6 max-w-2xl mx-auto">
                Get programmatic access to all MCP toolkits through our unified API gateway.
                Perfect for integration with AI assistants and custom applications.
              </p>
              <a
                href="https://tools.nickfixit.com/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View API Documentation
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}